import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import dayjs from "dayjs"
import localeDe from "dayjs/locale/de"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export const getCurrentDate = () => dayjs().locale(localeDe).format().split("+")[0]

interface PlanLabelData {
  selectedPlan?: { tier?: string; duration?: string } | null;
  planTier?: string;
  planDuration?: string;
  planName?: string;
}

export function getPlanLabel(data: PlanLabelData, plans: any[]): string {
  const tier = data.selectedPlan?.tier || data.planTier;
  const duration = data.selectedPlan?.duration || data.planDuration;
  if (tier && duration) {
    const capitalized = tier.charAt(0).toUpperCase() + tier.slice(1);
    return `${capitalized} — ${duration}`;
  }
  const match = plans.find(p => p.name === data.planName || p.title === data.planName);
  if (match) {
    const capitalized = match.tier.charAt(0).toUpperCase() + match.tier.slice(1);
    return `${capitalized} — ${match.duration}`;
  }
  return data.planName || '—';
}
export const getFormattedDate = (date: string) => dayjs(date).locale(localeDe).format().split("+")[0]
export const formatDate = (date: string) => new Date(date).toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" })

export function isValidArray(arr: unknown): boolean {
  return Array.isArray(arr) && arr.length > 0
}

export async function hashObject(obj: Record<string, any>) {
  try {
    const cleaned = stripFields(obj, ["updatedAt"])
    const json = stableStringify(cleaned)
    const encoder = new TextEncoder()
    const data = encoder.encode(json)

    const hashBuffer = await crypto.subtle.digest("SHA-256", data)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("")
  } catch (_err) {
    return []
  }
}

function stripFields<T>(obj: T, fieldsToRemove: string[]): T {
  if (Array.isArray(obj)) {
    return obj.map((item) => stripFields(item, fieldsToRemove)) as T
  } else if (obj && typeof obj === "object") {
    const cleaned: Record<string, any> = {}
    for (const key of Object.keys(obj as object)) {
      if (!fieldsToRemove.includes(key)) {
        cleaned[key] = stripFields((obj as Record<string, any>)[key], fieldsToRemove)
      }
    }
    return cleaned as T
  }
  return obj
}

function stableStringify(obj: Record<string, any>): string {
  if (Array.isArray(obj)) {
    return `[${obj.map(stableStringify).join(",")}]`
  } else if (obj && typeof obj === "object") {
    return `{${Object.keys(obj || {}).sort().map((k) => `"${k}":${stableStringify((obj as Record<string, any>)[k])}`).join(",")}}`
  } else {
    return JSON.stringify(obj)
  }
}

export async function getChangedIndices(oldArray: Record<string, any>[], newArray: Record<string, any>[]) {
  if (!isValidArray(oldArray) && !isValidArray(newArray)) {
    return []
  }

  const oldHashes = await Promise.all(oldArray.map(hashObject))
  const newHashes = await Promise.all(newArray.map(hashObject))

  const changed = []

  for (let i = 0; i < Math.max(oldArray.length, newArray.length); i++) {
    if (oldHashes[i] !== newHashes[i]) {
      changed.push(i)
    }
  }

  return changed
}

export const formatMarkdown = (text: string) => {
  return text
    ?.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.*?)\*/g, "<em>$1</em>")
    .replace(/`(.*?)`/g, '<code class="bg-muted px-1 py-0.5 rounded text-sm font-mono">$1</code>')
    .replace(/^### (.*$)/gm, '<h3 class="text-lg font-semibold mt-4 mb-2">$1</h3>')
    .replace(/^## (.*$)/gm, '<h2 class="text-xl font-semibold mt-4 mb-2">$1</h2>')
    .replace(/^# (.*$)/gm, '<h1 class="text-2xl font-bold mt-4 mb-2">$1</h1>')
    .replace(/^- (.*$)/gm, '<li class="ml-4 list-disc">$1</li>')
    .replace(/^\d+\. (.*$)/gm, '<li class="ml-4 list-decimal">$1</li>')
    .replace(/^> (.*$)/gm, '<blockquote class="border-l-4 border-muted pl-4 italic my-2">$1</blockquote>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-blue-600 underline hover:text-blue-800">$1</a>')
    .replace(/\n/g, "<br>")
}

export const sleep = (delay: number) => new Promise((resolve) => setTimeout(resolve, delay))

export const isTrue = (value: any) => value === "true" || value === true

export enum PageType {
  Dashboard = "dashboard",
  ProgressTracker = "progress-tracker",
  QuestionBank = "question-bank",
  PracticeTestBuilder = "exam-builder",
  Users = "users",
  Exams = "exam-builder",
  SourceManagement = "source-management",
  InterestManagement = "interest-management",
  FlaggedQuestions = "flagged-questions",
  Unknown = "unknown",
}

const matchers: { type: PageType; match: (path: string) => boolean }[] = [
  { type: PageType.Dashboard, match: path => path === "/dashboard" },
  { type: PageType.ProgressTracker, match: path => path.includes("progress-tracker") },
  { type: PageType.QuestionBank, match: path => path.includes("question-bank") },
  { type: PageType.PracticeTestBuilder, match: path => path.includes("exam-builder") },
  { type: PageType.SourceManagement, match: path => path.includes("source-management") },
  { type: PageType.InterestManagement, match: path => path.includes("interest-management") },
  { type: PageType.FlaggedQuestions, match: path => path.includes("flagged-questions") },
  // must come after more specific matchers above — "active-users" also contains "users"
  { type: PageType.Users, match: path => path.endsWith("/users") },
]

export const currentPage = (path: string): PageType => {
  const found = matchers.find(m => m.match(path))
  return found?.type ?? PageType.Unknown
}

export const groupChapters = (input: Array<number | string> | undefined | null): string => {
  if (!Array.isArray(input) || input.length === 0) return '';

  // Convert to numbers, filter out non-numeric values
  const nums = input
    .map(x => Number(x))
    .filter(n => Number.isFinite(n));

  if (nums.length === 0) return '';

  // Unique + sort
  const sorted = Array.from(new Set(nums)).sort((a, b) => a - b);

  const ranges: string[] = [];
  let start = sorted[0];
  let prev = sorted[0];

  for (let i = 1; i < sorted.length; i++) {
    const cur = sorted[i];

    if (cur === prev + 1) {
      // still in a consecutive run
      prev = cur;
      continue;
    }

    // close the previous run
    if (start === prev) {
      ranges.push(String(start));
    } else {
      ranges.push(`${start}-${prev}`);
    }

    // start a new run
    start = cur;
    prev = cur;
  }

  // push the final run
  if (start === prev) ranges.push(String(start));
  else ranges.push(`${start}-${prev}`);

  return ranges.join(', ');
}
